import {
  CalendarMonth,
  CheckBoxOutlineBlank,
  CorporateFare,
  FavoriteBorder,
  StarBorder,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Typography,
} from '@mui/material';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ChatBottomNav from '../components/chat/ChatBottomNav';
import AppHeader from '../components/layout/AppHeader';
import API_ENDPOINTS from '../config/api';
import { useAuth } from '../contexts/AuthContext';

interface FocusItem {
  id: string;
  text: string;
  action_id: string;
  habit_id: string;
  habit_title: string;
  completed: boolean;
}

interface HomeStats {
  day_streak: number;
  actions_remaining_today: number;
  checkins_this_week: number;
}

interface UserProfile {
  id: string;
  name: string;
  display_name?: string;
  date_joined?: string;
  selected_avatar_details?: {
    id: number;
    display_name: string;
    image_url: string;
  };
  active_membership?: {
    provider: {
      business_name: string;
    };
    site: {
      name: string;
      background_colour?: string;
      default_avatar?: {
        id: number;
        display_name: string;
        image_url: string;
      } | null;
    };
  } | null;
}

const FONT_FAMILY =
  'Poppins, Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif';

const STAT_CARD_SX = {
  bgcolor: 'rgba(255, 255, 255, 0.75)',
  borderRadius: '16px',
  filter: 'drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25))',
  px: 2,
  py: 2,
  textAlign: 'center' as const,
};

const HomePage: React.FC = () => {
  const { user, syncUser } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionCount, setSessionCount] = useState<number>(0);
  const [homeStats, setHomeStats] = useState<HomeStats | null>(null);

  const [focusItems, setFocusItems] = useState<FocusItem[]>([]);

  useEffect(() => {
    loadProfile();
    loadSessionCount();
    loadHomeStats();
    loadTodaysFocus();
  }, []);

  const loadProfile = async (): Promise<void> => {
    try {
      setLoading(true);
      const response = await axios.get(API_ENDPOINTS.AUTH.USER_INFO);
      setProfile(response.data.user);
      syncUser(response.data.user);
    } catch (err) {
      console.error('Failed to load profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadHomeStats = async (): Promise<void> => {
    try {
      const response = await axios.get(API_ENDPOINTS.HABITS.HOME_STATS);
      setHomeStats(response.data);
    } catch (err) {
      console.error('Failed to load home stats:', err);
    }
  };

  const loadTodaysFocus = async (): Promise<void> => {
    try {
      const response = await axios.get(API_ENDPOINTS.HABITS.TODAYS_FOCUS);
      setFocusItems(response.data.items || []);
    } catch (err) {
      console.error('Failed to load today\'s focus:', err);
    }
  };

  const loadSessionCount = async (): Promise<void> => {
    try {
      const response = await axios.get(
        '%s?ordering=-created_at&limit=200'.replace(
          '%s',
          API_ENDPOINTS.CONVERSATIONS.LIST,
        ),
      );
      const results = response.data.results || [];
      setSessionCount(results.length);
    } catch (err) {
      console.error('Failed to load session count:', err);
    }
  };

  const handleToggleFocus = (id: string): void => {
    const item = focusItems.find((f) => f.id === id);
    if (!item || item.completed) return;

    // Navigate to habit detail page so user can do a proper check-in
    // with self-score and optional notes
    navigate(`/habits/${item.habit_id}`);
  };

  if (loading) {
    return (
      <Box
        sx={{
          height: 'var(--app-height, 100vh)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const displayName =
    profile?.display_name?.trim() ||
    profile?.name?.trim() ||
    user?.display_name?.trim() ||
    user?.name?.trim() ||
    'Username';

  const memberSinceText = (() => {
    const joined = profile?.date_joined;
    if (joined) {
      const d = new Date(joined);
      const month = d.toLocaleString('default', { month: 'long' });
      return 'Member since %s %d'.replace('%s', month).replace('%d', String(d.getFullYear()));
    }
    return 'Member since —';
  })();

  const dayStreak = homeStats ? String(homeStats.day_streak) : '—';
  const actionsRemaining = homeStats ? String(homeStats.actions_remaining_today) : '—';
  const checkinsThisWeek = homeStats ? String(homeStats.checkins_this_week) : '—';
  const activeMembership = profile?.active_membership;
  const providerAccent = activeMembership?.site.background_colour || '#9ED8FF';
  const avatarDetails =
    profile?.selected_avatar_details ||
    activeMembership?.site.default_avatar ||
    null;

  return (
    <Box
      sx={{
        height: 'var(--app-height, 100vh)',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#FFFFFF',
      }}
    >
      <AppHeader
        title="Home"
        leftContent={
          <Button
            aria-label="SOS — crisis support"
            onClick={() => navigate('/sos')}
            sx={{
              minWidth: 0,
              px: 3,
              py: 1.25,
              bgcolor: '#B00020',
              color: '#FFFFFF',
              borderRadius: '32px',
              fontWeight: 700,
              fontSize: 16,
              lineHeight: 1,
              boxShadow: 'none',
              '&:hover': { bgcolor: '#8E001A', boxShadow: 'none' },
            }}
          >
            <Box
              sx={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                bgcolor: '#FFFFFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 0.75,
                flexShrink: 0,
                fontWeight: 900,
                fontSize: 15,
                color: '#B00020',
                lineHeight: 1,
              }}
            >
              !
            </Box>
            SOS
          </Button>
        }
      />

      <Box sx={{ flex: 1, overflow: 'auto', px: 2, pb: 3, pt: 1, WebkitOverflowScrolling: 'touch' }}>
        {/* Yellow panel */}
        <Box
          sx={{
            bgcolor: 'rgba(255, 212, 0, 0.65)',
            borderRadius: '25px',
            filter: 'drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25))',
            px: 2,
            py: 3,
            mt: 1,
          }}
        >
          {/* Avatar + username + member since */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2.5 }}>
            {/* Jeff avatar placeholder (stickman) */}
            <Box sx={{ position: 'relative', flexShrink: 0 }}>
              {activeMembership && (
                <Box
                  title={`${activeMembership.provider.business_name} / ${activeMembership.site.name}`}
                  sx={{
                    position: 'absolute',
                    top: -4,
                    left: -4,
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    bgcolor: providerAccent,
                    border: '2px solid #FFFFFF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1,
                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.22)',
                  }}
                >
                  <CorporateFare sx={{ fontSize: 15, color: '#FFFFFF' }} />
                </Box>
              )}
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '40px',
                  border: `3px solid ${activeMembership ? providerAccent : '#FFD400'}`,
                  overflow: 'hidden',
                  bgcolor: '#FFFFFF',
                }}
              >
                <Box
                  component="img"
                  src={avatarDetails?.image_url || '/static/default-avatar.png'}
                  alt={avatarDetails?.display_name || 'Avatar'}
                  sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              </Box>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontFamily: FONT_FAMILY,
                  fontWeight: 700,
                  fontSize: 36,
                  lineHeight: '120%',
                  letterSpacing: '-0.02em',
                  color: '#000000',
                }}
              >
                {displayName}
              </Typography>
              <Typography
                sx={{
                  fontFamily: FONT_FAMILY,
                  fontWeight: 400,
                  fontSize: 16,
                  lineHeight: '120%',
                  color: '#828282',
                  mt: 0.5,
                }}
              >
                {memberSinceText}
              </Typography>
            </Box>
          </Box>

          {/* Stat cards row */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 1.5,
              mb: 2.5,
            }}
          >
            {/* Day Streak */}
            <Box sx={STAT_CARD_SX}>
              <StarBorder sx={{ fontSize: 28, color: '#2196F3' }} />
              <Typography sx={{ fontWeight: 700, fontSize: 36, lineHeight: '120%' }}>
                {dayStreak}
              </Typography>
              <Typography sx={{ fontWeight: 600, fontSize: 13 }}>Day login streak</Typography>
            </Box>

            {/* Actions Remaining Today */}
            <Box sx={STAT_CARD_SX}>
              <FavoriteBorder sx={{ fontSize: 28, color: '#F44336' }} />
              <Typography sx={{ fontWeight: 700, fontSize: 36, lineHeight: '120%' }}>
                {actionsRemaining}
              </Typography>
              <Typography sx={{ fontWeight: 600, fontSize: 13 }}>Actions for today</Typography>
            </Box>

            {/* Check-ins This Week */}
            <Box sx={STAT_CARD_SX}>
              <CalendarMonth sx={{ fontSize: 28, color: '#4CAF50' }} />
              <Typography sx={{ fontWeight: 700, fontSize: 36, lineHeight: '120%' }}>
                {checkinsThisWeek}
              </Typography>
              <Typography sx={{ fontWeight: 600, fontSize: 13 }}>Check-ins this week</Typography>
            </Box>
          </Box>

          {/* Today's Focus */}
          <Box
            sx={{
              bgcolor: '#D5D5D5',
              borderRadius: '20px',
              px: 2.5,
              py: 2,
            }}
          >
            <Typography
              sx={{
                fontFamily: FONT_FAMILY,
                fontWeight: 800,
                fontSize: 22,
                mb: 1.5,
                color: '#000000',
              }}
            >
              Today&apos;s Focus Actions
            </Typography>

            {focusItems.length === 0 && (
              <Typography
                sx={{
                  fontFamily: FONT_FAMILY,
                  fontSize: 14,
                  fontWeight: 500,
                  color: '#555',
                  fontStyle: 'italic',
                }}
              >
                No actions to focus on yet! Talk to Jeff and go to the Habits page and activate suggested habits to see actions here...
              </Typography>
            )}

            {focusItems.map((item, index) => (
              <Box
                key={item.id}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  mb: index < focusItems.length - 1 ? 1.5 : 0,
                }}
              >
                {/* Numbered circle */}
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '14px',
                    bgcolor: '#FFD400',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    mt: 0.25,
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: 14,
                      color: '#000000',
                      lineHeight: 1,
                    }}
                  >
                    {index + 1}
                  </Typography>
                </Box>

                {/* Text */}
                <Typography
                  sx={{
                    flex: 1,
                    fontFamily: FONT_FAMILY,
                    fontSize: 15,
                    fontWeight: 600,
                    lineHeight: '140%',
                    color: '#000000',
                    textDecoration: item.completed ? 'line-through' : 'none',
                    opacity: item.completed ? 0.5 : 1,
                  }}
                >
                  {item.text}
                </Typography>

                {/* Checkbox */}
                <Checkbox
                  checked={item.completed}
                  onChange={() => handleToggleFocus(item.id)}
                  icon={
                    <CheckBoxOutlineBlank
                      sx={{ color: '#000000', fontSize: 22 }}
                    />
                  }
                  sx={{
                    p: 0,
                    mt: 0.25,
                    '& .MuiSvgIcon-root': { fontSize: 22 },
                    '&.Mui-checked .MuiSvgIcon-root': { color: '#FFD400' },
                  }}
                />
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <ChatBottomNav />
    </Box >
  );
};

export default HomePage;
